import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DSSheet, DSIcon } from '../design-system/primitives';
import PinGolfScore from './PinGolfScore';
import PinGolfHandicapTrend from './PinGolfHandicapTrend';
import { apiService } from '../services/apiService';
import { formatHandicap } from '../utils/pingolfScoring';
import './PinGolfHandicapAudit.css';

// "Why is my handicap that number." Every other PinGolf surface asserts the handicap; this one
// has to prove it, so it shows the blend term by term, the line it has traced over the season,
// and every card that moved it - each linking back to the scorecard itself.
//
// UNITS, because this screen is the one place they cannot be fudged: the stored index is a
// scoring average versus par (negative means the player beats par), and the scorecard shows
// its negation, because net = card - index = card + displayed. So the blend below is written
// entirely in CARD units (a card of +5 and an index of +5 mean the same thing), and the flip
// to scorecard strokes happens once, explicitly, in its own block. Mixing the two is what made
// "SCORE 0, HCP -2, NET +2" unreadable before - see toDisplayHandicap.

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Card-unit average, one decimal, signed the same way a card is.
const formatAverage = (value) => {
  if (value === null || value === undefined) return '-';
  const rounded = Number(value).toFixed(1);
  return Number(value) > 0 ? `+${rounded}` : rounded;
};

function contextFor(entry) {
  if (entry.weekNumber) {
    return `${entry.seasonName || 'League'} · Week ${entry.weekNumber}`;
  }
  return entry.eventTitle || 'PinGolf night';
}

function CardEntry({ entry, onOpenCard }) {
  const movement = entry.delta === null ? null : entry.delta;
  // Movement is stated in scorecard strokes, matching the rest of the sheet: a card worse than
  // your average pushes the index up in card units, which shows as MORE strokes on the card.
  const strokeMovement = movement === null ? null : -movement;
  const canOpen = Boolean(entry.cardId && entry.eventId);

  const body = (
    <>
      <span className="pg-hcp-row__main">
        <span className="pg-hcp-row__title">{contextFor(entry)}</span>
        <span className="pg-hcp-row__meta">
          {formatDateTime(entry.eventDate || entry.at)}
          {entry.cardNumber ? ` · card ${entry.cardNumber} of your history` : ''}
        </span>
      </span>
      <span className="pg-hcp-row__score">
        <PinGolfScore value={entry.cardResult} emphasis />
      </span>
      {canOpen && <DSIcon name="chevronRight" size="sm" tone="muted" />}
    </>
  );

  return (
    <li className="pg-hcp-entry">
      {canOpen ? (
        <button type="button" className="pg-hcp-row pg-hcp-row--button" onClick={() => onOpenCard(entry)}>
          {body}
        </button>
      ) : (
        <div className="pg-hcp-row">{body}</div>
      )}

      <dl className="pg-hcp-entry__facts">
        <div>
          <dt>Handicap that night</dt>
          <dd>{formatHandicap(entry.playingHandicapApplied)}</dd>
        </div>
        <div>
          <dt>Net</dt>
          <dd><PinGolfScore value={entry.net} /></dd>
        </div>
        <div>
          <dt>Index after</dt>
          <dd>
            {formatAverage(entry.previousIndex)} <span aria-hidden="true">→</span> {formatAverage(entry.newIndex)}
          </dd>
        </div>
        <div>
          <dt>Moved you</dt>
          <dd className={strokeMovement === null || Math.abs(strokeMovement) < 0.05 ? 'is-flat' : ''}>
            {strokeMovement === null || Math.abs(strokeMovement) < 0.05
              ? 'no change'
              : `${strokeMovement > 0 ? '+' : '-'}${Math.abs(strokeMovement).toFixed(1)} strokes`}
          </dd>
        </div>
      </dl>
    </li>
  );
}

function SeedEntry({ entry }) {
  const from = entry.previousSeed === null || entry.previousSeed === undefined
    ? 'no seed'
    : formatAverage(entry.previousSeed);
  const to = entry.newSeed === null || entry.newSeed === undefined
    ? 'no seed'
    : formatAverage(entry.newSeed);

  return (
    <li className="pg-hcp-entry pg-hcp-entry--seed">
      <div className="pg-hcp-row">
        <span className="pg-hcp-row__main">
          <span className="pg-hcp-row__title">Starting seed changed</span>
          <span className="pg-hcp-row__meta">
            {formatDateTime(entry.at)}
            {entry.actorName ? ` · by ${entry.actorName}` : ''}
          </span>
        </span>
      </div>
      <p className="pg-hcp-entry__note">
        Seed moved from {from} to {to}
        {entry.newSeedWeight ? `, counted as ${entry.newSeedWeight} ${entry.newSeedWeight === 1 ? 'round' : 'rounds'}` : ''}.
        {entry.note ? ` "${entry.note}"` : ''}
      </p>
    </li>
  );
}

function PinGolfHandicapAudit({ onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    apiService.getMyPingolfHandicap()
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your handicap history. Try again in a moment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const openCard = (entry) => {
    navigate(`/events/${entry.eventId}/pingolf-cards/${entry.cardId}`);
  };

  const current = data?.current;
  const formula = data?.formula;
  // Newest first for reading; the API hands them back oldest first because that is the order
  // the running calculation happened in, which is what the trend line needs.
  const entries = data?.entries || [];
  const newestFirst = [...entries].reverse();
  const hasSeed = formula && formula.seed !== null && formula.seed !== undefined;

  return (
    <DSSheet title="How your handicap works" onClose={onClose}>
      {loading && <p className="pg-hcp-empty">Loading your history...</p>}
      {error && <p className="pg-hcp-empty">{error}</p>}

      {!loading && !error && current && (
        <>
          <section className="pg-hcp-now">
            <div className="pg-hcp-now__hero">
              <span className="pg-hcp-now__value">{formatHandicap(current.playingHandicap)}</span>
              <span className="pg-hcp-now__label">strokes on your card right now</span>
            </div>
            <p className="pg-hcp-now__explain">
              Your net for a night is your card plus that number.
              {current.hasHistory
                ? ` It comes from ${current.cardsCounted} ${current.cardsCounted === 1 ? 'card' : 'cards'} you have turned in${hasSeed ? ', plus your starting seed' : ''}.`
                : ' You have not turned in a card yet, so this is entirely your starting seed.'}
            </p>
          </section>

          <section className="pg-hcp-block">
            <h3 className="pg-hcp-block__title">The arithmetic</h3>
            <p className="pg-hcp-block__lede">
              Everything here is in card scores, where a card of +5 and an index of +5 mean the
              same thing. Your index is simply the average of them.
            </p>

            <dl className="pg-hcp-math">
              {hasSeed && (
                <div className="pg-hcp-math__row">
                  <dt>
                    Starting seed
                    <span className="pg-hcp-math__hint">
                      {formatAverage(formula.seed)} counted {formula.seedWeight} {formula.seedWeight === 1 ? 'time' : 'times'}
                    </span>
                  </dt>
                  <dd>{formatAverage(formula.seedTotal)}</dd>
                </div>
              )}
              <div className="pg-hcp-math__row">
                <dt>
                  Your cards, added up
                  <span className="pg-hcp-math__hint">
                    {formula.cardCount} {formula.cardCount === 1 ? 'card' : 'cards'} counted
                  </span>
                </dt>
                <dd>{formatAverage(formula.cardTotal)}</dd>
              </div>
              <div className="pg-hcp-math__row pg-hcp-math__row--sum">
                <dt>
                  Divided by
                  <span className="pg-hcp-math__hint">
                    {hasSeed ? `${formula.seedWeight} seed + ${formula.cardCount} cards` : `${formula.cardCount} cards`}
                  </span>
                </dt>
                <dd>{formula.divisor}</dd>
              </div>
              <div className="pg-hcp-math__row pg-hcp-math__row--total">
                <dt>Handicap index</dt>
                <dd>{formatAverage(formula.index)}</dd>
              </div>
            </dl>

            <p className="pg-hcp-block__foot">
              That index of {formatAverage(formula.index)} rounds to {formatAverage(current.playingHandicap)},
              which is what the scorecard shows as {formatHandicap(current.playingHandicap)}: you average
              that far {Number(formula.index ?? 0) < 0 ? 'under' : 'over'} par, so the league
              {Number(formula.index ?? 0) < 0 ? ' adds those strokes to your card' : ' takes those strokes off your card'} to
              put you level with the field.
            </p>
          </section>

          {entries.length > 1 && (
            <section className="pg-hcp-block">
              <h3 className="pg-hcp-block__title">Over time</h3>
              <PinGolfHandicapTrend entries={entries} />
            </section>
          )}

          <section className="pg-hcp-block">
            <h3 className="pg-hcp-block__title">
              Every card that counted
              {data.cardCount > 0 && <span className="pg-hcp-block__count">{data.cardCount}</span>}
            </h3>
            {newestFirst.length === 0 ? (
              <p className="pg-hcp-empty">
                Nothing has moved your handicap yet. Your first finished card shows up here.
              </p>
            ) : (
              <ul className="pg-hcp-entries">
                {newestFirst.map((entry) => (
                  entry.kind === 'card'
                    ? <CardEntry key={entry.id} entry={entry} onOpenCard={openCard} />
                    : <SeedEntry key={entry.id} entry={entry} />
                ))}
              </ul>
            )}
            <p className="pg-hcp-block__foot">
              Only your counting card for a night lands here, and it counts from the moment you
              bought it - an unfinished card scores its unplayed holes at the worst result on the
              board, which is why walking away from one moves this so much. Tap any night to open
              the scorecard hole by hole.
            </p>
          </section>
        </>
      )}
    </DSSheet>
  );
}

export default PinGolfHandicapAudit;
